import { useEffect, useState } from "react";
import { EmptyState } from "@/design-system/components/ui";
import { formatDateInTz, formatCurrency } from "@/design-system/lib/format";
import { StatementDownloadBar } from "../components/StatementDownloadBar";
import { brokeragePortalApi, type BrokeragePayments } from "../api/brokerage";
import { LoadingTiles, StatTile, SubBrokerTable } from "../components/BrokerageBits";

// Brokerage / Sub-Broker PR-6 — the Payments tab: the firm's OWN full financials.
// Two distinct money views (do not conflate): the split-ledger firm totals (what
// Phoenix pays the firm + how it splits to sub-brokers — display/tracking, empty
// until a junior sub-broker exists) and the recognized commission carve across
// the roster's loans (what Phoenix actually paid).
export function BrokeragePaymentsTab() {
  const [data, setData] = useState<BrokeragePayments | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const d = await brokeragePortalApi.payments();
        if (!cancelled) setData(d);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Couldn't load your firm's payments.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) return <p className="text-small text-status-error">{error}</p>;
  if (!data) return <LoadingTiles />;

  const c = data.commissions;

  return (
    <div className="space-y-space-5">
      <div className="max-w-xs">
        <StatTile label="Gross (Phoenix → firm)">{formatCurrency(data.totalGross)}</StatTile>
      </div>
      <p className="text-caption text-gray-mid">
        Phoenix pays your firm the gross commission; sub-broker payouts settle off Phoenix.
      </p>

      <div className="grid gap-space-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label="Commission earned to date">{formatCurrency(c.totalEarned)}</StatTile>
        <StatTile label="Paid this year">
          {c.paidYearToDate != null ? formatCurrency(c.paidYearToDate) : "—"}
        </StatTile>
        <StatTile label="Loans paying">{c.loanCount}</StatTile>
        <StatTile label="Last paid">{c.lastPaidAt ? formatDateInTz(c.lastPaidAt) : "—"}</StatTile>
      </div>

      <StatementDownloadBar
        onDownload={(p) => brokeragePortalApi.statement(p)}
        filename={(p) => `brokerage-commissions-${p}.pdf`}
      />

      <div className="space-y-space-3">
        <h3 className="text-small font-semibold uppercase tracking-wide text-gray-mid">
          Gross by sub-broker ({data.subBrokers.length})
        </h3>
        {data.subBrokers.length === 0 ? (
          <EmptyState
            title="No sub-broker activity yet"
            description="Once your firm's sub-brokers source deals, the per-sub-broker gross appears here."
          />
        ) : (
          <SubBrokerTable rows={data.subBrokers} />
        )}
      </div>
    </div>
  );
}
